<script lang="ts">
  import type { BankMatch, BankMatchResult } from "../types";
  import Warning from "~icons/ph/warning-bold";
  import FooterButtons from "../../../components/workflow/FooterButtons.svelte";
  import ContinueButton from "../../../components/workflow/ContinueButton.svelte";
  import BankAccountInfo from "../elements/BankAccountInfo.svelte";
  import CloseButton from "../../../components/CloseButton.svelte";
  import { getWorkflow } from "../../../lib/workflow/workflowLib.svelte";

  let {
    bankMatch,
    phone,
    backToBankChoice = false,
  }: {
    bankMatch: BankMatch;
    phone: string;
    backToBankChoice: boolean;
  } = $props();

  const workflow = getWorkflow();

  const TITLES: Record<BankMatchResult, string> = {
    match: "Bank account verified",
    close_match: "Confirm bank account",
    no_match: "The name on this account doesn't match",
    user_data_error: "We couldn't find this account",
    api_error: "We couldn't verify your bank account",
    no_more_retries: "We can't verify this account online",
  };

  let result = $derived(bankMatch.result);

  function goToBankChoice() {
    workflow.step("bank-choice");
  }
</script>

<h2 class="ui-title">{TITLES[result]}</h2>

{#if result === "match"}
  <p>Your bank account was successfully verified! Use this bank account for your payment?</p>
{:else if result === "close_match"}
  <p>
    We want to ensure your payment goes to the right person: the name on your bank account is <b>slightly different</b> from
    our records. Use this bank account for your payment?
  </p>
{:else if result === "no_match"}
  <p>
    The name on the bank account you provided doesn't match the name on your JLG claim. Please re-enter your details, or
    call us at <a class="underline text-blue-500" href="tel:{phone}">{phone}</a> if you need help.
  </p>
{:else if result === "user_data_error"}
  <p>
    The bank details you entered don't appear to be valid. Please double-check your account number and sort code, then
    try again.
  </p>
  <p>
    If this issue persists please give us a call at <a class="underline text-blue-500" href="tel:{phone}">{phone}</a> and
    we'll work through this with you.
  </p>
{:else if result === "api_error"}
  <p>
    We’re sorry, but something went wrong on our end and we couldn't confirm your account details. Please re-enter your
    bank information to try again.
  </p>
  <p>
    If this issue persists please give us a call at <a class="underline text-blue-500" href="tel:{phone}">{phone}</a> and
    we'll work through this with you.
  </p>
{:else if result === "no_more_retries"}
  <p>
    We weren't able to verify your bank account after several attempts. To make sure your settlement reaches you safely,
    please give us a call at <a class="underline text-blue-500" href="tel:{phone}">{phone}</a> and we'll work through this
    with you.
  </p>
{/if}

{#if ["match", "close_match"].includes(result)}
  <BankAccountInfo {bankMatch} />

  {#if result === "close_match"}
    <div class="ui-alert ui-alert-warning">
      <Warning />
      <p>Make sure this is the correct account: you <b>won't</b> be able to switch it later.</p>
    </div>
  {/if}

  <input type="hidden" name="bank_account[id]" value={bankMatch.id} />

  <FooterButtons>
    <ContinueButton label="Confirm bank choice" />
  </FooterButtons>
{:else if result === "no_more_retries"}
  <div class="pt-40"></div>
  <FooterButtons back={false}>
    {#if backToBankChoice}
      <button class="btn btn-ghost" type="button" onclick={goToBankChoice}>Back</button>
    {:else}
      <CloseButton />
    {/if}
    <a class="btn btn-primary" href="tel:{phone}">Call us</a>
  </FooterButtons>
{:else}
  <div class="pt-40"></div>
  <FooterButtons back={false}>
    <button type="button" class="btn btn-primary ml-auto" onclick={() => workflow.prevStep()}>Try again</button>
  </FooterButtons>
{/if}
